import { useEffect, useState } from "react";
import { Printer, Mail, X } from "lucide-react";
import { toast } from "sonner";
import api, { peso, fmtErr } from "../lib/api";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

export default function ReceiptModal({ saleId, open, onClose, allowEmail = false }) {
  const [sale, setSale] = useState(null);

  useEffect(() => {
    if (open && saleId) {
      api.get(`/sales/${saleId}`).then((r) => setSale(r.data)).catch((e) => toast.error(fmtErr(e)));
    } else {
      setSale(null);
    }
  }, [open, saleId]);

  const emailReceipt = async () => {
    try {
      const { data } = await api.post(`/sales/${saleId}/email-receipt`);
      toast.success(data.message);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const s = sale;
  const settings = s?.settings || {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="receipt-modal">
        <div className="receipt-print bg-white p-5 font-mono text-[12px] leading-relaxed text-slate-800">
          {s ? (
            <>
              <div className="text-center border-b border-dashed border-slate-400 pb-2 mb-2">
                <p className="font-bold text-sm">{settings.store_name}</p>
                <p>{settings.address}</p>
                <p>Tel {settings.phone}</p>
                <p>VAT REG TIN: {settings.tin}</p>
                <p>{settings.bir_accred}</p>
              </div>
              <div className="flex justify-between"><span>OR No:</span><span className="font-bold" data-testid="receipt-number">{s.receipt_no}</span></div>
              <div className="flex justify-between"><span>Date:</span><span>{s.created_at?.slice(0, 16).replace("T", " ")}</span></div>
              <div className="flex justify-between"><span>Cashier:</span><span>{s.cashier_name}</span></div>
              <div className="flex justify-between"><span>Customer:</span><span>{s.customer_name}</span></div>
              {s.senior_id && <div className="flex justify-between"><span>SC/PWD ID:</span><span>{s.senior_id}</span></div>}
              <div className="border-t border-dashed border-slate-400 my-2" />
              {s.items.map((it, i) => (
                <div key={i} className="flex justify-between gap-2">
                  <span className="truncate">{it.name} x{it.qty}</span>
                  <span>{peso(it.line_total)}</span>
                </div>
              ))}
              <div className="border-t border-dashed border-slate-400 my-2" />
              <div className="flex justify-between"><span>Subtotal</span><span>{peso(s.subtotal)}</span></div>
              {s.discount_amount > 0 && (
                <div className="flex justify-between">
                  <span>{s.discount_type === "promo" ? "Promo" : s.discount_type.toUpperCase()} Discount</span>
                  <span>-{peso(s.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between"><span>VATable Sales</span><span>{peso(s.vatable_sales)}</span></div>
              <div className="flex justify-between"><span>VAT (12%)</span><span>{peso(s.vat_amount)}</span></div>
              <div className="flex justify-between"><span>VAT-Exempt</span><span>{peso(s.vat_exempt_sales)}</span></div>
              <div className="flex justify-between font-bold text-sm border-t border-dashed border-slate-400 mt-1 pt-1">
                <span>TOTAL</span><span data-testid="receipt-total">{peso(s.total)}</span>
              </div>
              {s.payments.map((p, i) => (
                <div key={i} className="flex justify-between">
                  <span>{p.method.replace("_", " ").toUpperCase()}{p.reference ? ` (${p.reference})` : ""}</span>
                  <span>{peso(p.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between"><span>CHANGE</span><span>{peso(s.change)}</span></div>
              <div className="border-t border-dashed border-slate-400 mt-2 pt-2 text-center">
                <p>THIS SERVES AS YOUR OFFICIAL RECEIPT</p>
                <p>Salamat! Come back soon.</p>
                {s.loyalty_points_earned > 0 && <p>You earned {s.loyalty_points_earned} paw points!</p>}
              </div>
            </>
          ) : (
            <p className="text-center py-8">Loading receipt…</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 p-3 bg-[#E2F5F5]">
          {allowEmail && s?.customer_email && (
            <Button variant="outline" size="sm" onClick={emailReceipt} data-testid="receipt-email-btn">
              <Mail className="w-4 h-4 mr-1" /> E-Receipt
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="receipt-print-btn">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <Button size="sm" className="bg-[#0E7C84] hover:bg-[#093F46]" onClick={onClose} data-testid="receipt-close-btn">
            <X className="w-4 h-4 mr-1" /> Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
